import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import { CombinedGraphQLErrors, ServerError, ServerParseError } from '@apollo/client/errors';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { HttpLink } from '@apollo/client/link/http';
import { ApolloProvider } from '@apollo/client/react';
import { relayStylePagination } from '@apollo/client/utilities';
import { useMsal } from '@azure/msal-react';

import { protectedResources } from '@/config/authConfig';
import { env } from '@/env';
import { requestAccessToken } from '@/providers/MsalContextProvider/requestAccessToken/index';

import { ApolloErrorContext, type ApolloErrorState } from './ApolloErrorContext';

type ApolloContextProviderProps = {
  children: ReactNode;
};

let cachedClient: ApolloClient | undefined;
// Mutable pointer updated by the active component instance on mount/unmount.
// Decouples the long-lived cached ErrorLink from any specific component's setState.
let currentSetApolloError: ((error: ApolloErrorState | null) => void) | null = null;
// Mutable pointer for the token getter so the cached authLink always uses the
// latest MSAL instance without reconstructing the entire ApolloClient.
// Not cleared on unmount — in-flight requests must be able to complete auth
// even while the provider tears down (no React state is touched by this callback).
let currentGetToken: (() => Promise<string | undefined>) | null = null;

const buildErrorLink = (): ErrorLink =>
  new ErrorLink(({ error, operation }) => {
    // Route through the mutable pointer so remounted components receive errors
    // even though the ErrorLink itself is reused from the module-level cache.
    if (!currentSetApolloError) return;

    const operationName = operation.operationName || 'anonymous-operation';

    if (CombinedGraphQLErrors.is(error)) {
      const details = error.errors.map((graphQLError) => graphQLError.message);
      currentSetApolloError({
        kind: 'graphql',
        operationName,
        message: 'GraphQL resolver/validation error',
        details,
      });

      console.error('[Apollo GraphQL error]', {
        operationName,
        details,
      });
      return;
    }

    if (ServerError.is(error) || ServerParseError.is(error)) {
      currentSetApolloError({
        kind: 'network',
        operationName,
        message: error.message,
      });

      console.error('[Apollo network error]', {
        operationName,
        message: error.message,
      });
      return;
    }

    currentSetApolloError({
      kind: 'unknown',
      operationName,
      message: error.message,
    });

    console.error('[Apollo unknown error]', {
      operationName,
      message: error.message,
    });
  });

const buildClient = (): ApolloClient => {
  if (cachedClient) return cachedClient;

  const authLink = new SetContextLink(async (prevContext) => {
    // Dereference at dispatch time so instance changes are always picked up.
    // currentGetToken is always set before any request fires (set in useEffect on mount),
    // but the ternary guards against the theoretical pre-mount window.
    /* v8 ignore next -- @preserve */
    const token = currentGetToken ? await currentGetToken() : undefined;
    const headers = (prevContext.headers ?? {}) as Record<string, string>;
    return {
      headers: {
        ...headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };
  });

  const errorLink = buildErrorLink();

  cachedClient = new ApolloClient({
    link: ApolloLink.from([errorLink, authLink, new HttpLink({ uri: env.mlGraphEndpoint })]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            file_disposition_projects: relayStylePagination(['nameSearch', 'statuses']),
          },
        },
      },
    }),
    defaultOptions: { watchQuery: { fetchPolicy: 'cache-and-network' } },
  });

  return cachedClient;
};

export const ApolloContextProvider = ({ children }: ApolloContextProviderProps) => {
  const { instance } = useMsal();
  const [error, setError] = useState<ApolloErrorState | null>(null);

  // Wire the active component's setter into the module-level pointer.
  // Cleared on unmount so the stale setter is never called after teardown.
  useEffect(() => {
    currentSetApolloError = setError;
    return () => {
      currentSetApolloError = null;
    };
  }, [setError]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const getToken = useCallback(() => requestAccessToken(instance, protectedResources.graphql.scopes), [instance]);

  // Keep the module-level pointer current so the cached authLink always calls
  // the latest getToken when the MSAL instance changes.
  useEffect(() => {
    currentGetToken = getToken;
  }, [getToken]);

  // Stable for the app lifetime — authLink reads currentGetToken at dispatch
  // time, so no rebuild is needed when the instance changes.
  const client = useMemo(() => buildClient(), []);

  const contextValue = useMemo(
    () => ({
      error,
      clearError,
    }),
    [clearError, error],
  );

  return (
    <ApolloErrorContext.Provider value={contextValue}>
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </ApolloErrorContext.Provider>
  );
};
